import React, { useEffect, useRef, useState } from 'react';
import { MessagesSquare, X, Send } from 'lucide-react';
import { fetchMyThread, sendMyMessage } from '../services/apiClient';

/**
 * Tester-facing chat with the admin team. Shown only to testers when messaging
 * is enabled. Polls the thread every few seconds while open (lightweight, not
 * real-time).
 */
export default function TesterChatWidget({ user, unread = 0 }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const isTester = Boolean(user?.permissions?.isTester);

  async function load() {
    const result = await fetchMyThread();
    if (result?.ok) setMessages(result.messages || []);
  }

  useEffect(() => {
    if (!open) {
      clearInterval(pollRef.current);
      return undefined;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- polling fetch while chat is open
    load();
    pollRef.current = setInterval(load, 5000);
    return () => clearInterval(pollRef.current);
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  async function handleSend(event) {
    event.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    const result = await sendMyMessage(body);
    setSending(false);
    if (result?.ok) { setDraft(''); load(); }
  }

  if (!isTester) return null;

  return (
    <div className="fixed bottom-5 right-5 z-[90] print:hidden">
      {open && (
        <div className="mb-3 w-[340px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-mentor-border/70 bg-mentor-surface shadow-2xl shadow-black/40">
          <div className="flex items-center justify-between bg-mentor-accent px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <MessagesSquare size={17} />
              <span className="font-display font-bold text-sm">Message the Admin</span>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="rounded-lg p-1 hover:bg-white/15"><X size={16} /></button>
          </div>

          <div ref={scrollRef} className="h-72 overflow-y-auto px-3 py-3 space-y-2 bg-mentor-bg/40">
            {messages.length === 0 ? (
              <p className="text-center text-xs text-mentor-muted mt-8">No messages yet. Say hello or report what you&rsquo;re testing.</p>
            ) : (
              messages.map((m) => (
                <div key={m.id} className={`flex ${m.fromAdmin ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${m.fromAdmin ? 'bg-mentor-surface border border-mentor-border/60 text-mentor-text' : 'bg-mentor-accent text-white'}`}>
                    {m.fromAdmin && <p className="text-[10px] font-display font-bold uppercase tracking-wider opacity-70 mb-0.5">{m.fromName || 'Admin'}</p>}
                    <p className="whitespace-pre-wrap break-words">{m.body}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          <form onSubmit={handleSend} className="flex items-center gap-2 border-t border-mentor-border/60 p-2">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Type a message…"
              className="flex-1 rounded-xl border border-mentor-border/60 bg-mentor-bg px-3 py-2 text-sm text-mentor-text placeholder:text-mentor-muted/70 focus:border-mentor-accent/60 focus:outline-none"
            />
            <button type="submit" disabled={sending} className="rounded-xl bg-mentor-accent p-2.5 text-white hover:bg-mentor-accent/90 disabled:opacity-60"><Send size={15} /></button>
          </form>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex items-center gap-2 rounded-full bg-mentor-accent px-4 py-3 font-display text-sm font-bold text-white shadow-lg shadow-mentor-accent/30 hover:bg-mentor-accent/90"
      >
        <MessagesSquare size={17} /> Admin Chat
        {unread > 0 && !open && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full bg-signal-red px-1 text-[10px] font-bold leading-[18px] text-white text-center">{unread}</span>
        )}
      </button>
    </div>
  );
}
